import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  LIST_STALE_TIME_MS,
  nullOnNotFound,
  useApiClient,
  useCurrentUser,
  usePermissions,
} from '@klareospace/pharos-lib-front-utils';
import type { TenantBillingConfigurationView } from '@klareospace/pharos-lib-types';

import { useAuth } from '&/auth';
import { billingKeys, createTenantBillingApi } from '../api';

/**
 * The tenant admin's own billing configuration — `null` (not an error) when the tenant has no
 * configuration yet or lacks `billing:read-own` (the backend 404s / the query never fires).
 */
export function useOwnTenantBillingConfiguration(): UseQueryResult<TenantBillingConfigurationView | null> {
  const { isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();
  const { user } = useCurrentUser(isAuthenticated);
  const tenantId = user?.tenantId ?? null;
  const client = useApiClient('bill');

  return useQuery({
    enabled: isAuthenticated && hasPermission('billing:read-own') && tenantId !== null,
    queryFn: (): Promise<TenantBillingConfigurationView | null> => {
      if (!tenantId) {
        throw new Error('[use-own-tenant-billing-configuration] tenantId must be set before this query runs.');
      }
      return nullOnNotFound(() => createTenantBillingApi(client).getOwnConfiguration(tenantId));
    },
    queryKey: billingKeys.own(tenantId),
    staleTime: LIST_STALE_TIME_MS,
  });
}
